import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMarkAllRead, useMarkRead, useNotifications } from '../lib/api/notifications';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import { fmtRelative } from '../lib/dates';

const iconForKind = (kind: string) =>
  kind === 'task_assigned' ? 'task'
  : kind === 'overlap_alert' ? 'alert'
  : kind === 'newsletter_gap' ? 'mail'
  : kind === 'booking_signed_up' ? 'user'
  : 'inbox';

const Notifications = () => {
  const navigate = useNavigate();
  const { data: notifications = [], isLoading } = useNotifications();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();
  const unread = notifications.filter((n) => !n.read_at).length;

  const groupedByDay = useMemo(() => {
    const groups = new Map<string, typeof notifications>();
    for (const n of notifications) {
      const day = n.created_at.slice(0, 10);
      const arr = groups.get(day) ?? [];
      arr.push(n);
      groups.set(day, arr);
    }
    return Array.from(groups.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [notifications]);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Inbox</div>
          <h1 className="page-title">Notifications</h1>
          <p className="page-sub">
            {notifications.length} total · {unread} unread.
          </p>
        </div>
        {unread > 0 && (
          <Button variant="ghost" onClick={() => markAllRead.mutate()}>
            Mark all as read
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-4)' }}>
          Loading…
        </div>
      ) : notifications.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-4)' }}>
          Nothing in your inbox yet. Assign yourself a task or create two overlapping sitewide
          campaigns to see notifications flow.
        </div>
      ) : (
        groupedByDay.map(([day, group]) => (
          <div key={day} className="section">
            <div className="section-head">
              <div className="section-title">{fmtRelative(day)}</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                {group.length} notification{group.length === 1 ? '' : 's'}
              </div>
            </div>
            <div className="card">
              {group.map((n) => (
                <div
                  key={n.id}
                  className="alert-item"
                  style={{
                    background: n.read_at ? undefined : 'var(--bg-subtle)',
                    cursor: n.link_url ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (!n.read_at) markRead.mutate(n.id);
                    if (n.link_url) navigate(n.link_url);
                  }}
                >
                  <div className="alert-icon" style={{
                    background: n.kind === 'overlap_alert' ? 'var(--warn-bg)' : 'var(--bg-active)',
                    color: n.kind === 'overlap_alert' ? 'var(--warn)' : 'var(--ink-3)',
                  }}>
                    <Icon name={iconForKind(n.kind)} size={13} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12.8, fontWeight: n.read_at ? 400 : 500, color: 'var(--ink)' }}>
                      {n.title}
                    </div>
                    {n.body && (
                      <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 2 }}>
                        {n.body}
                      </div>
                    )}
                  </div>
                  {!n.read_at && (
                    <div style={{
                      width: 6, height: 6, borderRadius: 50, background: 'var(--sitewide)',
                      flexShrink: 0, marginTop: 10,
                    }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default Notifications;
